import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Business, Info, Search } from '@mui/icons-material';
import { Alert, Box, Button, TextField, Typography } from '@mui/material';
import type { AdminProviderResponse, AdminProviderLookupUser } from '@store/generated/admin';

import {
  useGetOrganizationProvidersQuery,
  useGetProviderInfoQuery,
  useSearchProvidersQuery,
} from '../store/api';

// Union type to handle both provider response types
type ProviderResult = AdminProviderResponse | AdminProviderLookupUser;

interface SearchFormProps {
  onProviderSelect?: (provider: ProviderResult) => void;
}

type SearchType = 'email' | 'provider_id' | 'organization_id' | 'name';

const detectSearchType = (query: string): SearchType => {
  if (query.includes('@')) return 'email';
  if (query.includes('|')) return 'provider_id';
  // UUID pattern: 8-4-4-4-12 characters (with hyphens)
  if (query.match(/^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i))
    return 'organization_id';
  return 'name';
};

export function SearchForm({ onProviderSelect }: SearchFormProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  const navigate = useNavigate();

  const searchType = useMemo(() => detectSearchType(searchQuery), [searchQuery]);

  // Use different queries based on search type
  const {
    data: searchResults,
    isLoading: searchLoading,
    error: searchError,
  } = useSearchProvidersQuery(searchQuery, {
    skip:
      !hasSearched ||
      searchQuery.trim().length === 0 ||
      searchType === 'organization_id' ||
      searchType === 'provider_id',
  });

  const {
    data: providerResult,
    isLoading: providerLoading,
    error: providerError,
  } = useGetProviderInfoQuery(
    { providerId: searchQuery },
    {
      skip: !hasSearched || searchQuery.trim().length === 0 || searchType !== 'provider_id',
    },
  );

  const {
    data: orgResults,
    isLoading: orgLoading,
    error: orgError,
  } = useGetOrganizationProvidersQuery(
    { organizationId: searchQuery },
    {
      skip: !hasSearched || searchQuery.trim().length === 0 || searchType !== 'organization_id',
    },
  );

  // Combine results and loading states
  const results = useMemo(() => {
    if (searchType === 'provider_id') {
      // providerResult is now AdminProviderLookupUser directly (due to transformation)
      return providerResult ? [providerResult] : [];
    } else if (searchType === 'organization_id') {
      // orgResults is ListResponseAdminProviderResponse, so we need to access .items
      return orgResults?.items || [];
    } else {
      // searchResults is AdminProviderResponse[], so we can use it directly
      return searchResults || [];
    }
  }, [searchType, providerResult, orgResults, searchResults]);

  const isLoading = useMemo(() => {
    if (searchType === 'provider_id') return providerLoading;
    if (searchType === 'organization_id') return orgLoading;
    return searchLoading;
  }, [searchType, providerLoading, orgLoading, searchLoading]);

  const error = useMemo(() => {
    if (searchType === 'provider_id') return providerError;
    if (searchType === 'organization_id') return orgError;
    return searchError;
  }, [searchType, providerError, orgError, searchError]);

  const handleSearch = useCallback(() => {
    if (searchQuery.trim()) {
      setHasSearched(true);
    }
  }, [searchQuery]);

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleProviderClick = (provider: ProviderResult) => {
    if (onProviderSelect) {
      onProviderSelect(provider);
    } else {
      // Ensure providerId is properly encoded and create a unique navigation
      const encodedProviderId = encodeURIComponent(provider.provider_id);
      const targetUrl = `/admin-v2/provider/${encodedProviderId}`;

      // Use providerId directly in URL and pass provider data in state
      navigate(targetUrl, {
        state: {
          user: provider,
          providerId: provider.provider_id,
          timestamp: Date.now(), // Add timestamp to ensure unique navigation
        },
        replace: false, // Ensure we create a new history entry
      });
    }
  };

  const handleSearchQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    if (value.trim() === '') {
      setHasSearched(false);
    }
  };

  const getSearchHint = () => {
    switch (searchType) {
      case 'provider_id':
        return {
          color: 'info' as const,
          title: 'Provider ID Search',
          message: 'Searching by provider ID. This will look up a specific provider.',
        };
      case 'organization_id':
        return {
          color: 'success' as const,
          title: 'Organization Search',
          message:
            'Searching by organization ID. This will show all providers in the organization.',
        };
      case 'email':
        return {
          color: 'primary' as const,
          title: 'Email Search',
          message: 'Searching by email address.',
        };
      default:
        return null;
    }
  };

  const searchHint = getSearchHint();

  const handleOrganizationNavigate = useCallback(
    (orgId: string) => {
      navigate(`/admin-v2/organization/${orgId}`);
    },
    [navigate],
  );

  return (
    <div className="w-full">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-gray-900 mb-4">Admin Search</h2>
        <p className="text-lg text-gray-600">
          Search by email, name, provider ID, or organization ID
        </p>
        <div className="mt-4 flex justify-center gap-4">
          <Button
            variant="outlined"
            startIcon={<Business />}
            onClick={() => navigate('/admin-v2/organizations')}
          >
            Browse Organizations
          </Button>
        </div>
      </div>

      <div className="max-w-2xl mx-auto mb-8">
        <div className="relative">
          <TextField
            type="text"
            value={searchQuery}
            onChange={handleSearchQueryChange}
            onKeyDown={handleKeyPress}
            fullWidth
            placeholder="Search by Email, Name, Provider ID, or Organization ID..."
            variant="outlined"
          />
        </div>

        {/* Show search type hint */}
        {searchHint && !hasSearched && (
          <Box sx={{ mt: 2, p: 2, bgcolor: `${searchHint.color}.light`, borderRadius: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Info color={searchHint.color} fontSize="small" />
              <Typography variant="body2" color={`${searchHint.color}.dark`}>
                <strong>{searchHint.title}:</strong> {searchHint.message}
              </Typography>
            </Box>
          </Box>
        )}

        <div className="flex justify-center mt-4">
          <Button
            onClick={handleSearch}
            disabled={!searchQuery.trim() || isLoading}
            className="px-6 py-3 text-base"
          >
            <Search className="mr-2 h-4 w-4" />
            {isLoading ? 'Searching...' : 'Search'}
          </Button>
        </div>
      </div>

      {/* Show error if search fails */}
      {hasSearched && error && (
        <div className="max-w-2xl mx-auto mb-4">
          <Alert severity="error">Search failed: Unknown error</Alert>
        </div>
      )}

      {hasSearched && (
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Search Results</h3>
            <p className="text-sm text-gray-500">
              Found {results.length} provider{results.length !== 1 ? 's' : ''}
              {searchType === 'organization_id' ? ' in organization' : ' matching your search'}
              {searchQuery && <span className="font-medium"> &quot;{searchQuery}&quot;</span>}
            </p>
          </div>
          <div className="divide-y divide-gray-200">
            {results.length === 0 ? (
              <div className="px-6 py-8 text-center">
                <p className="text-gray-500 mb-2">
                  No{' '}
                  {searchType === 'organization_id'
                    ? 'providers found in this organization'
                    : 'providers found matching your search criteria'}
                  .
                </p>
                <div className="text-sm text-gray-400">
                  {searchType === 'organization_id' ? (
                    <p>The organization may be empty or the ID may be incorrect.</p>
                  ) : searchType === 'provider_id' ? (
                    <>
                      <p>Provider ID not found.</p>
                      <p>Check that the provider ID is correct and the provider exists.</p>
                    </>
                  ) : (
                    <p>Try a different email address or name.</p>
                  )}
                </div>
              </div>
            ) : (
              results.map((provider: ProviderResult) => (
                <div
                  key={provider.provider_id}
                  className="px-6 py-4 hover:bg-gray-50 cursor-pointer"
                  onClick={() => {
                    handleProviderClick(provider);
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleProviderClick(provider);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  aria-label={`Select provider ${provider.name || 'Unknown Provider'}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                          <span className="text-lg font-semibold text-blue-600">
                            {provider.name ? provider.name.charAt(0) : '?'}
                          </span>
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {provider.name || 'Unknown Provider'}
                        </div>
                        <div className="text-sm text-gray-500">{provider.email || 'No email'}</div>
                        <div className="text-xs text-gray-400">
                          Provider ID: {provider.provider_id || 'N/A'}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-4">
                      <div className="text-right text-sm">
                        <div className="text-gray-900">{provider.note_count || 0} notes</div>
                        <div className="text-gray-500">
                          Last activity:{' '}
                          {provider.last_login_time
                            ? new Date(provider.last_login_time).toLocaleDateString()
                            : 'Never'}
                        </div>
                      </div>
                      <div className="text-gray-400">
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Organization hint when UUID detected */}
      {searchType === 'organization_id' && searchQuery.trim() && !hasSearched && (
        <Box className="mt-4 max-w-2xl mx-auto">
          <Alert severity="info" icon={<Business />}>
            <Typography variant="body2">
              Detected organization ID. You can:
            </Typography>
            <Box className="mt-2">
              <Button
                size="small"
                variant="outlined"
                onClick={() => handleOrganizationNavigate(searchQuery.trim())}
                className="mr-2"
              >
                Go to Organization Details
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => navigate('/admin-v2/organizations')}
              >
                Browse All Organizations
              </Button>
            </Box>
          </Alert>
        </Box>
      )}
    </div>
  );
}
